import { Dispatch } from '@reduxjs/toolkit';
import { setCpf, setVehicle, setVistoriaId, resetInspection } from '../store/vistoriaSlice';
import { setAssociadoId, updateAssociadoData } from '../store/associadoSlice';
import { resetChecklistAcessorios } from '../store/checklistAcessoriosSlice';
import { totalFotosEsperado } from './vistoriaLegendas';
import { formatarDataCurta } from './data';

/**
 * Reidratação do fluxo de vistoria a partir de um item de painel (at-32).
 *
 * O fluxo de vistoria (`/vistoria/captura` em diante) lê veículo/associado do
 * Redux, então qualquer tela que ofereça "vistoriar" precisa preencher o mesmo
 * conjunto de campos antes de navegar. Estava inline no painel `/vistoria`
 * (at-24) e a carteira precisou do mesmo comportamento — extraído para cá para
 * que as duas telas não divirjam no que preenchem.
 *
 * ✅ **A cópia inline do painel foi APAGADA na at-63** (frente D/D2), que era o que
 * este cabeçalho pedia desde a at-32. Hoje as **duas** portas de entrada do fluxo
 * — o painel `/vistoria` e a `/carteira` — hidratam pelo mesmo caminho, o que
 * importa porque as chaves que elas preenchem são **contrato** com as telas do
 * fluxo (`plan-d` V2): divergir aqui quebra a retomada em silêncio.
 *
 * 📌 O painel tinha **duas** funções — `iniciarVistoria` (sem limpar o checklist)
 * e `hidratarFluxo` (limpando) — e o `vistoriaId` opcional daqui mapeia exatamente
 * nas duas: sem id é vistoria nova (o `resetInspection` já basta), com id é
 * retomada, e aí as respostas de OUTRA vistoria que estejam no Redux **precisam**
 * sair da frente, porque a tela de checklist lê o slice e não o banco.
 */
export interface VeiculoParaVistoria {
  carroId: string;
  placa?: string | null;
  modelo?: string | null;
  anoModelo?: number | null;
  anoFabricacao?: number | null;
  tipoVeiculo?: string | null;
  associado: {
    id?: string | null;
    nomeCompleto?: string | null;
    cpfCnpj?: string | null;
  };
}

/** Preenche o Redux com o veículo/associado do item, do zero. */
export function hidratarFluxoVistoria(
  dispatch: Dispatch,
  item: VeiculoParaVistoria,
  vistoriaId?: string | null,
) {
  dispatch(resetInspection());
  dispatch(setCpf(item.associado.cpfCnpj ?? ''));
  dispatch(
    updateAssociadoData({
      nomeCompleto: item.associado.nomeCompleto ?? '',
      cpfCnpj: item.associado.cpfCnpj ?? '',
      email: '',
    }),
  );
  if (item.associado.id) dispatch(setAssociadoId(String(item.associado.id)));

  dispatch(
    setVehicle({
      id: item.carroId,
      placa: item.placa ?? '',
      modelo: item.modelo ?? undefined,
      anoModelo: item.anoModelo ?? undefined,
      anoFabricacao: item.anoFabricacao ?? undefined,
      tipoVeiculo: item.tipoVeiculo || '',
    }),
  );

  // Retomada: o checklist é lido do banco pela própria tela, então o que estiver
  // no Redux de um fluxo anterior tem de sair da frente.
  if (vistoriaId) {
    dispatch(resetChecklistAcessorios());
    dispatch(setVistoriaId(vistoriaId));
  }
}

/**
 * Tudo que ainda falta na vistoria, em texto — fotos incluídas.
 *
 * O `proximoPasso` do backend só olha checklist/assinatura/laudo; a completude
 * das fotos não entra nele porque a lista canônica de legendas vive no
 * frontend (`vistoriaLegendas.ts`). Sem juntar as duas coisas, a tela diria
 * apenas "falta gerar o laudo" numa vistoria que também está sem foto.
 */
export function pendenciasDaVistoria(
  proximoPasso: 'CHECKLIST' | 'ASSINATURA' | 'LAUDO' | null,
  quantidadeFotos: number,
  tipoVeiculo?: string | null,
): string[] {
  const pendencias: string[] = [];

  const faltamFotos = Math.max(0, totalFotosEsperado(tipoVeiculo) - quantidadeFotos);
  if (faltamFotos > 0) {
    pendencias.push(faltamFotos === 1 ? '1 foto' : `${faltamFotos} fotos`);
  }

  if (proximoPasso === 'CHECKLIST') pendencias.push('o checklist');
  else if (proximoPasso === 'ASSINATURA') pendencias.push('a assinatura');
  else if (proximoPasso === 'LAUDO') pendencias.push('gerar o laudo');

  return pendencias;
}

/**
 * Data curta, sempre em `pt-BR`.
 *
 * ⚠️ Estava copiada em **4** telas do fluxo, e uma delas (`LaudoHtml`) chamava
 * `toLocaleString()` **sem locale** — o formato saía conforme o navegador
 * (`plan-d` V13). Uma função só, um formato só.
 *
 * 📌 **Desde a at-66 ela delega para `utils/data.ts`**, que é o formato de data
 * do app inteiro: a tela de Acompanhamento de Oficina tinha a **quinta** cópia
 * desta mesma função, e unificá-las aqui evita que as duas famílias de tela
 * divirjam no dia em que o formato mudar. O nome fica: é como as três telas de
 * vistoria a conhecem, e o comportamento é idêntico.
 */
export function formatarDataVistoria(iso: string): string {
  return formatarDataCurta(iso);
}

/**
 * Variantes de `Chip` que o estado de uma vistoria pode assumir.
 *
 * 🔴 **`perigo` NÃO está aqui, e é decisão de produto.** O CSS antigo pintava
 * "Associado pendente no SGA", "aguardando envio ao SGA" e o contador "sem
 * vistoria" de **VERMELHO** (`vistoria-badge-alerta`/`vistoria-chip-alerta`).
 * Nada disso é erro — é *"ainda não"*, e a spec 02 §2.3 é explícita: *"vermelho
 * não é erro… escolha uma linguagem visual que não pareça alarme: a maioria dos
 * registros novos passa alguns instantes em vermelho por natureza"*. Pendência é
 * **âmbar** (`pendente`). Vermelho fica reservado a falha de verdade, que este
 * painel não tem.
 */
export type VarianteEstadoVistoria = 'ok' | 'pendente' | 'neutro';

export interface RotuloEstado {
  texto: string;
  variante: VarianteEstadoVistoria;
}

/**
 * O estado da vistoria de um veículo, em **texto + variante** (at-63).
 *
 * Precedente exato do `rotuloStatus` da at-56 e do `rotuloSituacaoCarro` da
 * at-37: a regra de "o que este estado se chama e de que cor ele é" mora num
 * lugar só, para o painel, o modal de detalhes e a carteira (quando migrar)
 * **nunca divergirem sobre o mesmo veículo**.
 *
 * ⚠️ Chip **sempre com texto**, nunca só cor (spec 02 §9) — é por isso que o
 * retorno traz `texto` obrigatório.
 */
export function rotuloEstadoVistoria(item: {
  vistoria?: { data: string; concluida: boolean } | null;
}): RotuloEstado {
  if (!item.vistoria) return { texto: 'Sem vistoria', variante: 'neutro' };

  if (item.vistoria.concluida) {
    return {
      texto: `Realizada em ${formatarDataVistoria(item.vistoria.data)}`,
      variante: 'ok',
    };
  }

  return {
    texto: `Em andamento desde ${formatarDataVistoria(item.vistoria.data)}`,
    variante: 'pendente',
  };
}

/**
 * Chip de envio ao SGA do laudo ou das fotos.
 *
 * ⚠️ "Aguardando" é `pendente`, **não** `perigo`: o envio é feito pelo cron a
 * cada 2 horas e não exige ação nenhuma do consultor (at-24/at-31) — é o caso
 * canônico do que a spec chama de "não parecer alarme".
 */
export function rotuloEnvioSga(enviado: boolean, oQue: 'Laudo' | 'Fotos'): RotuloEstado {
  return enviado
    ? { texto: `${oQue} ${oQue === 'Laudo' ? 'enviado' : 'enviadas'} ao SGA`, variante: 'ok' }
    : { texto: `${oQue} aguardando envio ao SGA`, variante: 'pendente' };
}

/**
 * O associado precisa estar no SGA antes da vistoria (`POST /vistorias/create`
 * exige `codigoAssociado`). Era vermelho; é pendência.
 */
export const ROTULO_ASSOCIADO_PENDENTE_SGA: RotuloEstado = {
  texto: 'Associado pendente no SGA',
  variante: 'pendente',
};

/**
 * A vistoria **como está no BANCO** — o mínimo que decide as bolinhas.
 *
 * 🔴 Tipo ESTRUTURAL de propósito: casa com `VistoriaPorIdResponse` sem importar
 * de `api/`, o que mantém `situacaoDasEtapasDaVistoria` pura e testável sem mock
 * de rede.
 */
export interface VistoriaNoBanco {
  fotos?: Array<unknown> | null;
  accessories?: { items?: Array<{ status?: string | null }> | null } | null;
  signature_image_url?: string | null;
  pdf_image_url?: string | null;
}

/**
 * O que a tela sabe sobre a vistoria ao desenhar as bolinhas.
 *
 * `vistoria: null` e `leituraFalhou: true` são coisas **diferentes**, e a
 * distinção é o que evita bolinha muda (`plan-d` V11):
 *   • `null` → não existe vistoria ainda (primeira visita à captura);
 *   • `leituraFalhou` → existe, mas não conseguimos conferir o estado dela.
 */
export interface EstadoDaVistoria {
  vistoria: VistoriaNoBanco | null;
  leituraFalhou?: boolean;
  tipoVeiculo?: string | null;
}

/** Uma etapa da vistoria está incompleta? Lê **só o banco**. */
function etapaDaVistoriaIncompleta(estado: EstadoDaVistoria, etapa: number): boolean {
  const v = estado.vistoria;
  if (!v) return true;

  switch (etapa) {
    case 1: {
      // ⚠️ Só o que está PERSISTIDO conta. Foto capturada e ainda não enviada
      // vive em estado local da tela e desaparece num F5 — contá-la faria a
      // bolinha afirmar um progresso que o banco não tem.
      const fotos = Array.isArray(v.fotos) ? v.fotos.length : 0;
      return fotos < totalFotosEsperado(estado.tipoVeiculo);
    }
    case 2: {
      const itens = Array.isArray(v.accessories?.items) ? v.accessories!.items! : [];
      // Nenhum item respondido, ou algum sem resposta — o mesmo critério do
      // `resolverProximoPasso` do backend (at-24).
      return itens.length === 0 || itens.some((item) => !item?.status);
    }
    case 3:
      return !v.signature_image_url;
    case 4:
      return !v.pdf_image_url;
    default:
      return false;
  }
}

/**
 * Pendências e travas das 4 bolinhas da vistoria (at-63).
 *
 * Espelha `situacaoDasEtapasDaAdesao` (`utils/adesaoFluxo.ts`) — mesma assinatura,
 * mesmo contrato de retorno —, mas com uma diferença de fundo: **a fonte da
 * verdade aqui é o BANCO, não o Redux.** É o princípio da at-24 (*"estado real,
 * lido do banco"*), e o motivo é concreto: as fotos vivem em estado local até o
 * upload responder, o checklist é regravado por `PATCH` e o laudo por outra tela.
 * Perguntar ao Redux daria bolinha verde para trabalho que não existe no servidor.
 *
 * ⚠️ **Pendência só é reportada para etapa ATRÁS da atual** (regra da at-62): a
 * etapa à frente está incompleta por definição, e pintá-la de vermelho seria
 * alarme sobre o futuro.
 *
 * ⚠️ **Sem vistoria no banco, as etapas 2 a 4 são DESABILITADAS, não pendentes.**
 * Elas não têm o que mostrar: o checklist e a assinatura são gravados por
 * `vistoriaId`, e sem ele abriria uma tela vazia — um beco sem saída. O motivo vai
 * em texto no nome acessível do botão.
 *
 * ⚠️ **Leitura do banco falhando também desabilita, com motivo** — nunca deixa
 * bolinha muda nem, pior, verde por omissão.
 */
export function situacaoDasEtapasDaVistoria(
  estado: EstadoDaVistoria,
  etapaAtual: number,
): { pendentes: number[]; desabilitadas: Record<number, string> } {
  const desabilitadas: Record<number, string> = {};

  // Não conseguimos conferir nada: trava tudo (menos a atual) dizendo por quê.
  if (estado.leituraFalhou) {
    [1, 2, 3, 4].forEach((etapa) => {
      if (etapa !== etapaAtual) {
        desabilitadas[etapa] = 'não foi possível conferir o estado desta vistoria';
      }
    });
    return { pendentes: [], desabilitadas };
  }

  if (!estado.vistoria) {
    [2, 3, 4].forEach((etapa) => {
      if (etapa !== etapaAtual) desabilitadas[etapa] = 'envie as fotos primeiro';
    });
    return { pendentes: [], desabilitadas };
  }

  const pendentes: number[] = [];
  for (let etapa = 1; etapa < etapaAtual; etapa += 1) {
    if (etapaDaVistoriaIncompleta(estado, etapa)) pendentes.push(etapa);
  }

  // Trava da at-24: assinar antes de responder o checklist deixaria o laudo com
  // assinatura e sem respostas — e o consultor teria de assinar DE NOVO depois.
  if (etapaAtual !== 3 && etapaDaVistoriaIncompleta(estado, 2)) {
    desabilitadas[3] = 'responda o checklist primeiro';
  }

  return { pendentes, desabilitadas };
}
